import type { GridMatch } from '@/types/tournament.model';
import { fitsAvailability } from '@/utils/availability-fit';
import type { CellModel } from './grid-model';

export type DropState = 'ok' | 'warn';

export type DropVerdict = {
  state: DropState;
  /**
   * What to tell the organizer. Can be non-empty with an 'ok' state: the block and court notes
   * don't change the colour, but the organizer still needs to read them.
   */
  reason: string;
};

/**
 * Whether the match can land on the cell, and with what warning.
 *
 * **The colour answers one question: can both pairs play there?** That is what the organizer is
 * looking for while dragging. A full cell or an undeclared slot used to turn it amber too, which
 * hid the answer exactly when it mattered — a match the pairs *can* play still read as a problem
 * just because the courts were already taken. Those two stay as notes in `reason`, with a green
 * cell.
 *
 * **Nothing blocks a drop.** Court overbooking only warns — the generator marks `OVERBOOKED`
 * instead of failing, and blocking on capacity would leave the organizer unable to move
 * anything exactly when the grid is full, which is when they need to the most. An undeclared
 * slot only warns for the same reason: the backend's `moverPartidosEnGrilla` never validates
 * that the target time falls in a declared block, and the fine-grained modal doesn't either.
 *
 * The reasons accumulate instead of cascading through mutually exclusive branches, so a cell
 * that is undeclared *and* full *and* outside a pair's availability says all three.
 */
export function dropVerdictFor(match: GridMatch, cell: CellModel): DropVerdict {
  // Exclude the match being dragged: when the cell under evaluation is its own origin cell,
  // it's already counted in `cell.matches` even though dropping it back there adds nothing.
  const occupied = cell.matches.filter((m) => m.matchId !== match.matchId).length;
  const full = cell.courtsQuantity !== null && occupied >= cell.courtsQuantity;

  const team1Ok = fitsAvailability(match.team1Availability, cell.date, cell.startTime);
  const team2Ok = fitsAvailability(match.team2Availability, cell.date, cell.startTime);
  // `null` is "didn't declare availability", not "can't": it doesn't count as a problem.
  const someoneCannot = team1Ok === false || team2Ok === false;

  const reasons: string[] = [];
  if (someoneCannot) reasons.push('Alguna pareja no declaró esta franja.');
  if (!cell.declared) reasons.push('El bloque no declara esta franja.');
  if (full) reasons.push('No quedan canchas libres en esta franja.');

  return { state: someoneCannot ? 'warn' : 'ok', reason: reasons.join(' ') };
}
